import { type FC } from 'react';
import { Link } from 'react-router-dom';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

const TermsOfServicePage: FC = () => {
  usePageMeta({ title: 'Terms of Service', description: 'Quanthop terms of service. Read the terms and conditions that govern use of the Quanthop platform.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-[680px] mx-auto relative z-10 text-center">
          <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
            Terms of Service
          </h1>

          <p className="text-[15px] text-gray-300/70 leading-[1.7]">
            Please read these terms carefully before using Quanthop.
          </p>

          <p className="text-[13px] text-gray-400/60 mt-6">
            Last updated: 8 March 2026
          </p>
        </div>
      </section>

      {/* ════════════════ TERMS CONTENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[720px] mx-auto text-gray-700 text-[15px] leading-[1.8]">
          <p className="mb-10 text-gray-500">
            These Terms of Service (&ldquo;Terms&rdquo;) govern your access to
            and use of the Quanthop platform, website, and related services
            (collectively, the &ldquo;Service&rdquo;). By accessing or using
            Quanthop, you agree to be bound by these Terms. If you do not agree
            with these Terms, you must not use the Service.
          </p>

          {/* ── 1 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            1. About Quanthop
          </h2>
          <p className="mb-4">
            Quanthop provides a research platform for developing, testing,
            validating, and monitoring systematic trading strategies. The
            platform includes tools for backtesting, parameter optimization,
            validation workflows, and strategy monitoring.
          </p>
          <p className="mb-4 font-medium text-gray-900">
            Quanthop is a software research platform and does not provide
            investment advice or execute trades on behalf of users.
          </p>

          {/* ── 2 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            2. Eligibility
          </h2>
          <p className="mb-3">To use the Service you must:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>be at least 18 years old</li>
            <li>have the legal capacity to enter into a binding agreement</li>
            <li>comply with all applicable laws and regulations in your jurisdiction</li>
          </ul>
          <p>
            You are responsible for ensuring that your use of the Service is
            permitted in your country.
          </p>

          {/* ── 3 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            3. Accounts
          </h2>
          <p className="mb-3">
            To access certain features of the Service you must create an
            account. You agree to:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>provide accurate and complete information</li>
            <li>maintain the confidentiality of your login credentials</li>
            <li>notify us immediately of any unauthorised use of your account</li>
          </ul>
          <p>
            You are responsible for all activities that occur under your
            account.
          </p>

          {/* ── 4 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            4. Subscription and Billing
          </h2>
          <p className="mb-3">
            Access to certain features of Quanthop may require a paid
            subscription. By subscribing you agree that:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>subscription fees will be billed in advance</li>
            <li>subscriptions renew automatically unless cancelled</li>
            <li>
              pricing and billing terms are described on the{' '}
              <Link to="/pricing" className="text-[#9d4edd] hover:underline">
                pricing page
              </Link>
            </li>
          </ul>
          <p className="mb-4">
            You may cancel your subscription at any time. Access will remain
            active until the end of the current billing period.
          </p>
          <p className="font-medium text-gray-900">
            Unless otherwise stated, payments are non-refundable.
          </p>

          {/* ── 5 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            5. Acceptable Use
          </h2>
          <p className="mb-3">You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>use the Service for illegal activities</li>
            <li>attempt to reverse engineer or exploit the platform</li>
            <li>disrupt or interfere with platform infrastructure</li>
            <li>scrape or copy data from the platform without permission</li>
            <li>share or resell access to your account</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that violate
            these rules.
          </p>

          {/* ── 6 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            6. Intellectual Property
          </h2>
          <p className="mb-3">
            All software, design, and platform features of Quanthop are the
            intellectual property of Quanthop. This includes:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>the Quanthop platform</li>
            <li>algorithms and infrastructure</li>
            <li>branding and design</li>
            <li>documentation and content</li>
          </ul>
          <p className="mb-4">
            You retain ownership of any strategies, code, or content you upload
            to the platform.
          </p>
          <p>
            By using the Service, you grant Quanthop a limited licence to
            process your data for the purpose of operating the Service.
          </p>

          {/* ── 7 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            7. Financial Disclaimer
          </h2>
          <p className="mb-3 font-medium text-gray-900">
            Quanthop is a research and analysis platform only.
          </p>
          <p className="mb-3">The Service:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>does not provide financial advice</li>
            <li>does not recommend investments</li>
            <li>does not execute trades</li>
            <li>does not guarantee profitability</li>
          </ul>
          <p className="mb-4">
            All trading and investment decisions are made solely at your own
            risk.
          </p>
          <p className="mb-4 font-medium text-gray-900">
            Past performance results generated by the platform do not guarantee
            future results.
          </p>
          <p>
            Users are solely responsible for evaluating strategies and managing
            risk.
          </p>

          {/* ── 8 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            8. Platform Availability
          </h2>
          <p className="mb-3">
            We aim to provide a reliable platform but cannot guarantee
            uninterrupted service. The Service may occasionally be unavailable
            due to:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>maintenance</li>
            <li>system updates</li>
            <li>infrastructure issues</li>
            <li>external dependencies</li>
          </ul>
          <p>
            Quanthop is not liable for losses resulting from service
            interruptions.
          </p>

          {/* ── 9 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            9. Limitation of Liability
          </h2>
          <p className="mb-3">
            To the maximum extent permitted by law, Quanthop shall not be
            liable for:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>financial losses</li>
            <li>trading losses</li>
            <li>indirect or consequential damages</li>
            <li>loss of profits or revenue</li>
            <li>loss of data</li>
          </ul>
          <p>Use of the Service is at your own risk.</p>

          {/* ── 10 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            10. Termination
          </h2>
          <p className="mb-3">
            We may suspend or terminate your account if:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>you violate these Terms</li>
            <li>you misuse the platform</li>
            <li>required by law</li>
          </ul>
          <p>You may stop using the Service at any time.</p>

          {/* ── 11 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            11. Changes to the Service
          </h2>
          <p className="mb-4">
            We may modify, update, or discontinue features of the Service at
            any time.
          </p>
          <p>
            We will make reasonable efforts to provide notice when significant
            changes occur.
          </p>

          {/* ── 12 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            12. Changes to These Terms
          </h2>
          <p className="mb-4">
            We may update these Terms from time to time.
          </p>
          <p className="mb-4">
            When changes occur, the updated Terms will be posted on this page
            with a revised &ldquo;Last updated&rdquo; date.
          </p>
          <p>
            Continued use of the Service after updates constitutes acceptance
            of the revised Terms.
          </p>

          {/* ── 13 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            13. Governing Law
          </h2>
          <p className="mb-4">
            These Terms are governed by the laws of <strong>Ireland</strong>.
          </p>
          <p>
            Any disputes arising from these Terms will be subject to the
            jurisdiction of the courts of Ireland.
          </p>

          {/* ── 14 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            14. Contact
          </h2>
          <p className="mb-3">
            If you have questions about these Terms, you may contact us at:
          </p>
          <ul className="space-y-1.5">
            <li>
              <strong>Email:</strong>{' '}
              <a
                href="mailto:support@quanthop.com"
                className="text-[#9d4edd] hover:underline"
              >
                support@quanthop.com
              </a>
            </li>
            <li>
              <strong>Website:</strong>{' '}
              <a
                href="https://quanthop.com"
                className="text-[#9d4edd] hover:underline"
              >
                quanthop.com
              </a>
            </li>
          </ul>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default TermsOfServicePage;
